import { clsx } from 'clsx';
import {
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  Globe,
  Code,
  Package,
  UserRound,
  LayoutPanelTop,
} from 'lucide-react';
import type { TicketOriginType } from '@centraoneplus/shared-types';

/** The fields a source badge reads; every ticket shape in the app has these. */
export interface TicketSourceFields {
  origin?: string | null;
  originName?: string | null;
  originType?: TicketOriginType | string | null;
  channel?: string | null;
}

export interface TicketSourceDisplay {
  /** origin slug (or the channel when the ticket predates origin) */
  value: string;
  label: string;
  kind: TicketOriginType;
}

const CHANNEL_LABELS: Record<string, string> = {
  api: 'API',
  agent: 'Agent',
  email: 'Email',
  chat: 'Chat',
  voice: 'Voice',
  phone: 'Voice',
  portal: 'Portal',
  web: 'Web',
  whatsapp: 'WhatsApp',
  facebook: 'Facebook',
  twitter: 'X (Twitter)',
};

const titleCase = (value: string) =>
  value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

/**
 * Resolve how a ticket's source is shown. ticket-service sets origin/originName
 * on every ticket (and backfilled existing ones), so the channel fallback only
 * covers a response that has not been updated yet — it mirrors the server's
 * channel → origin table (phone → Voice) so the two never disagree.
 */
export function getTicketSourceDisplay(ticket: TicketSourceFields): TicketSourceDisplay | null {
  const kind: TicketOriginType =
    ticket.originType === 'product' || ticket.originType === 'agent' ? ticket.originType : 'channel';
  if (ticket.origin) {
    return {
      value: ticket.origin,
      label: ticket.originName || CHANNEL_LABELS[ticket.origin] || titleCase(ticket.origin),
      kind,
    };
  }
  if (ticket.channel) {
    const value = ticket.channel === 'phone' ? 'voice' : ticket.channel;
    return { value, label: CHANNEL_LABELS[ticket.channel] || titleCase(ticket.channel), kind: 'channel' };
  }
  return null;
}

const CHANNEL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  email: Mail,
  chat: MessageCircle,
  voice: Phone,
  phone: Phone,
  portal: LayoutPanelTop,
  web: Globe,
  api: Code,
  whatsapp: MessageSquare,
  facebook: MessageCircle,
  twitter: MessageCircle,
};

const KIND_STYLES: Record<TicketOriginType, string> = {
  // Products stand out: they are the integrations a tenant wired up itself.
  product: 'text-violet-700 bg-violet-50 dark:text-violet-300 dark:bg-violet-900/30',
  channel: 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-800',
  agent: 'text-sky-700 bg-sky-50 dark:text-sky-300 dark:bg-sky-900/30',
};

const KIND_LABELS: Record<TicketOriginType, string> = {
  product: 'Product',
  channel: 'Channel',
  agent: 'Created by an agent',
};

export function getTicketSourceIcon(display: Pick<TicketSourceDisplay, 'value' | 'kind'>) {
  if (display.kind === 'product') return Package;
  if (display.kind === 'agent') return UserRound;
  return CHANNEL_ICONS[display.value] || Globe;
}

interface TicketSourceBadgeProps {
  ticket: TicketSourceFields;
  /** Text before the label, e.g. "Created via". */
  prefix?: string;
  /** Appended after the label, e.g. " · key Billing sync". */
  suffix?: string;
  size?: 'sm' | 'md';
  className?: string;
}

/** Ticket source chip: icon + label, coloured by kind (product / channel / agent). */
export function TicketSourceBadge({
  ticket,
  prefix,
  suffix,
  size = 'sm',
  className,
}: TicketSourceBadgeProps) {
  const display = getTicketSourceDisplay(ticket);
  if (!display) return null;
  const Icon = getTicketSourceIcon(display);

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-md font-medium max-w-full',
        size === 'sm' ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-1 text-sm',
        KIND_STYLES[display.kind],
        className,
      )}
      title={`${KIND_LABELS[display.kind]}: ${display.label}`}
      data-origin={display.value}
      data-origin-type={display.kind}
    >
      <Icon className={clsx('flex-shrink-0', size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5')} aria-hidden />
      <span className="truncate">
        {prefix ? `${prefix} ` : ''}
        {display.label}
        {suffix ?? ''}
      </span>
    </span>
  );
}

export default TicketSourceBadge;
